// Da Vinci Dashboard (Project List)
// Lists user's Da Vinci projects and allows creating new ones.

import { DaVinciService } from "../DaVinciService.js";
import { waitForAuthReady } from "../../../auth.js"; // [FIX] Auth Sync

import { APP_VERSION } from "../../../version.js"; // [FIX] Import Version

export const DaVinciDashboard = async () => {
    console.log("[DaVinci] Dashboard Initializing...");

    // Safety: Force remove splash if it exists (in case router missed it)
    const splash = document.querySelector('.mirinae-splash');
    if (splash) splash.remove();

    const container = document.createElement('div');
    container.className = 'min-h-screen animate-fade-in relative';

    // Background
    const bg = document.createElement('div');
    bg.className = "absolute inset-0 bg-cover bg-center z-0";
    bg.style.backgroundImage = "url('/login.webp')";
    bg.style.filter = "brightness(0.3) blur(2px)";
    container.appendChild(bg);

    const content = document.createElement('div');
    content.className = "relative z-10 p-8 max-w-7xl mx-auto text-gray-100";
    container.appendChild(content);

    // Header
    const header = document.createElement('div');
    header.className = "flex justify-between items-center mb-8";
    header.innerHTML = `
        <div>
            <h1 class="text-3xl font-bold text-white mb-2 drop-shadow-md">
                Da Vinci Studio <span class="text-amber-500 text-lg ml-2">${APP_VERSION}</span>
            </h1>
            <p class="text-gray-300 drop-shadow">시네-노벨(Cine-Novel) 시리즈 관리</p>
        </div>
        <button id="btn-create-series" class="bg-amber-600 hover:bg-amber-500 text-white font-bold py-2 px-6 rounded-full transition-all flex items-center gap-2 shadow-lg">
            <span class="material-symbols-outlined">add</span>
            새 시리즈 만들기
        </button>
    `;
    content.appendChild(header);

    // Grid
    const grid = document.createElement('div');
    grid.className = "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6";
    content.appendChild(grid);

    // Load Series
    const loadSeries = async () => {
        grid.innerHTML = `<div class="col-span-3 text-center py-20 text-gray-300"><span class="animate-spin inline-block mr-2">⏳</span> 시리즈 불러오는 중...</div>`;

        try {
            if (!window.user) await waitForAuthReady; // [FIX] Wait for Firebase Auth to settle

            if (!window.user) {
                grid.innerHTML = `<div class="col-span-3 text-center py-20 text-gray-400">로그인이 필요합니다.</div>`;
                return;
            }

            // Timeout Race
            const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Timeout")), 8000));
            const res = await Promise.race([
                DaVinciService.listMySeries(window.user.uid),
                timeoutPromise
            ]);

            grid.innerHTML = '';

            if (!res || res.length === 0) {
                grid.innerHTML = `
                    <div class="col-span-3 text-center py-20 border-2 border-dashed border-gray-600 rounded-xl bg-black/40 backdrop-blur-sm">
                        <span class="material-symbols-outlined text-5xl text-gray-500 mb-4">library_books</span>
                        <p class="text-gray-300 mb-4 text-lg">아직 생성된 시리즈가 없습니다.</p>
                        <button class="text-amber-400 hover:text-amber-300 font-bold underline" id="btn-create-empty">지금 시리즈 시작하기</button>
                    </div>
                `;
                const emptyBtn = grid.querySelector('#btn-create-empty');
                if (emptyBtn) emptyBtn.onclick = createSeries;
                return;
            }

            // Render Function separated for clarity
            renderGrid(res);

        } catch (err) {
            console.error(err);
            grid.innerHTML = `
                <div class="col-span-3 text-red-400 bg-black/50 p-4 rounded text-center">
                    <p class="font-bold mb-2">오류가 발생했습니다.</p>
                    <p class="text-sm">${err.message}</p>
                    <button class="mt-4 px-4 py-2 bg-gray-700 rounded hover:bg-gray-600" onclick="window.location.reload()">새로고침</button>
                </div>`;
        }
    };

    const renderGrid = (seriesList) => {
        seriesList.forEach(s => {
            const card = document.createElement('div');
            card.className = "bg-black/60 border border-gray-700 rounded-xl overflow-hidden hover:border-amber-500/80 transition-all cursor-pointer group relative backdrop-blur-md shadow-xl";
            // [FIX] Use local fallback instead of external placeholder
            const thumb = s.metadata.thumbnail;

            card.innerHTML = `
                    <div class="aspect-video bg-zinc-800 relative overflow-hidden flex items-center justify-center">
                        ${thumb ?
                    `<img src="${thumb}" class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-80 group-hover:opacity-100">` :
                    `<span class="material-symbols-outlined text-zinc-600 text-5xl">image</span>`
                }
                        <div class="absolute top-2 right-2 flex gap-1">
                            ${s.status === 'published' ? '<span class="px-2 py-1 bg-green-900/80 text-green-400 text-xs rounded font-bold backdrop-blur-md">연재중</span>' : '<span class="px-2 py-1 bg-gray-800/80 text-gray-400 text-xs rounded font-bold backdrop-blur-md">준비중</span>'}
                        </div>
                    </div>
                    <div class="p-5">
                        <h3 class="text-xl font-bold text-white mb-1 truncate">${s.title || '무제 시리즈'}</h3>
                        <p class="text-sm text-gray-400 mb-4">최근 수정: ${new Date(s.updatedAt?.seconds * 1000).toLocaleDateString()}</p>
                        <div class="flex justify-between items-center mt-4">
                             <button class="text-sm text-red-400 hover:text-red-300 flex items-center gap-1 btn-delete-series px-2 py-1 rounded hover:bg-red-900/20">
                                <span class="material-symbols-outlined text-[16px]">delete</span> 삭제
                             </button>
                             <button class="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded text-sm transition-colors font-bold shadow-md">
                                에피소드 관리
                             </button>
                        </div>
                    </div>
                `;

            // Click Card -> Episode List
            card.onclick = (e) => {
                if (e.target.closest('.btn-delete-series')) return;
                window.router.navigate(`/davinci/series/${s.id}`);
            };

            // Delete Action
            const delBtn = card.querySelector('.btn-delete-series');
            delBtn.onclick = async (e) => {
                e.stopPropagation();
                if (confirm(`'${s.title}' 시리즈를 삭제하시겠습니까?\n포함된 모든 에피소드가 삭제될 수 있습니다.`)) {
                    await DaVinciService.deleteSeries(s.id);
                    loadSeries();
                }
            };

            grid.appendChild(card);
        });
    }

    const createSeries = async () => {
        if (!window.user) return;
        const newTitle = prompt("새 시리즈 제목을 입력하세요:", "새로운 이야기");
        if (!newTitle) return;

        // Create Series Doc
        const res = await DaVinciService.saveSeries({ title: newTitle }, window.user);
        if (res.success) {
            window.router.navigate(`/davinci/series/${res.id}`);
        } else {
            alert("시리즈 생성 실패: " + res.error);
        }
    };

    header.querySelector('#btn-create-series').onclick = createSeries;

    // Initial Load - Defer slightly to ensure render
    setTimeout(loadSeries, 100);

    return container;
};
